import React from "react";
import { EmissionsTrendPanel } from "./EmissionsTrendPanel";
import { TopContributingKpis } from "./TopContributingKpis";
import { scopeColorAt } from "./chartPalette";
import { EMISSION_UNIT, formatTonnes } from "./emissionUnits";
import * as S from "./scopeDashboard.styles";

const MONTHS_IN_FY = 12;

/**
 * Full carbon-footprint dashboard for a single scope: a hero card (total +
 * category split), a monthly emissions-trend chart, and the top contributing
 * KPIs. Purely presentational — all data arrives via `payload`.
 * @param {{total_emission:number,categories:object,monthly:object[],monthlyByKpi:object[],topKpis:object[]}} props.payload
 * @param {string} [props.fyLabel] - e.g. "FY 2025–2026"
 * @param {boolean} [props.loading]
 * @param {boolean} [props.error]
 */
export const ScopeDashboard = ({ payload, fyLabel, loading, error }) => {
  if (loading) {
    return <S.Skeleton aria-hidden="true" />;
  }
  if (error || !payload) {
    return null;
  }

  const total = Number(payload.total_emission || 0);
  const categories = payload.categories || {};
  const catKeys = Object.keys(categories);
  const monthly = payload.monthly || [];
  const monthlyByKpi = payload.monthlyByKpi || [];
  const kpis = payload.topKpis || [];
  const topKpis = kpis.slice(0, 6);
  // With one category the composition bar, the category card and its 100% share
  // all restate the total — name the category beside the total instead.
  const multiCategory = catKeys.length > 1;
  const soleCategory = catKeys.length === 1 ? catKeys[0] : null;
  const soleKpi = topKpis.length === 1;

  return (
    <S.Wrap>
      {soleCategory ? (
        <S.Hero>
          <SoleCategoryHero
            total={total}
            category={soleCategory}
            color={scopeColorAt(0)}
            monthly={monthly}
            fyLabel={fyLabel}
          />
        </S.Hero>
      ) : (
        <S.Hero>
          <S.HeroTop>
            <S.HeroTotal>
              <S.HeroLabel>
                Total Emission{fyLabel ? ` · ${fyLabel}` : ""}
              </S.HeroLabel>
              <S.HeroValue>
                {formatTonnes(total)} <span>{EMISSION_UNIT}</span>
              </S.HeroValue>
            </S.HeroTotal>

            {multiCategory && (
              <S.HeroCats>
                <S.HeroBar>
                  {catKeys.map((k, i) => {
                    const pct = total > 0 ? (Number(categories[k]) / total) * 100 : 0;
                    return <S.BarSeg key={k} $width={pct} $color={scopeColorAt(i)} />;
                  })}
                </S.HeroBar>
                <S.HeroBarNote>
                  Composition · {catKeys.length} categories
                </S.HeroBarNote>
              </S.HeroCats>
            )}
          </S.HeroTop>

          {multiCategory && (
            <S.StatGrid>
              {catKeys.map((k, i) => (
                <CategoryStat
                  key={k}
                  name={k}
                  value={Number(categories[k])}
                  total={total}
                  color={scopeColorAt(i)}
                />
              ))}
            </S.StatGrid>
          )}
        </S.Hero>
      )}

      <S.Grid $single={soleKpi}>
        <EmissionsTrendPanel
          monthly={monthly}
          monthlyByKpi={monthlyByKpi}
          categories={catKeys}
          kpis={kpis}
          fyLabel={fyLabel}
        />

        {!soleKpi && (
          <S.Panel>
            <S.PanelHead>
              <S.PanelTitles>
                <S.PanelTitle>Top contributing KPIs</S.PanelTitle>
                <S.PanelSub>Share of scope total</S.PanelSub>
              </S.PanelTitles>
            </S.PanelHead>
            <TopContributingKpis items={topKpis} />
          </S.Panel>
        )}
      </S.Grid>
    </S.Wrap>
  );
};

/**
 * Hero for a scope whose emissions all sit in one category. The split bar and
 * per-category card would only restate the total, so the row carries period
 * metrics derived from the monthly trend instead.
 * @param {number} props.total - scope total in kg CO₂e
 * @param {string} props.category - the scope's only category
 * @param {string} props.color - category series colour
 * @param {object[]} props.monthly - by-category monthly rows
 * @param {string} [props.fyLabel]
 */
const SoleCategoryHero = ({ total, category, color, monthly, fyLabel }) => {
  const months = monthly.map((row) => Number(row[category] || 0));
  const missingMonths = Math.max(MONTHS_IN_FY - months.length, 0);
  const peakIndex = months.reduce(
    (best, value, i) => (value > months[best] ? i : best),
    0,
  );

  return (
    <S.StatGrid>
      <S.StatCard $accent={color}>
        <S.StatLabel>
          <S.StatSwatch $color={color} />
          Total emission{fyLabel ? ` · ${fyLabel}` : ""}
        </S.StatLabel>
        <S.StatValue $lg>
          {formatTonnes(total)}
          <span>{EMISSION_UNIT}</span>
        </S.StatValue>
        <S.StatFoot>All from {category}</S.StatFoot>
      </S.StatCard>

      <S.StatCard>
        <S.StatLabel>Reported months</S.StatLabel>
        <S.StatValue>
          {months.length}
          <span>of {MONTHS_IN_FY}</span>
        </S.StatValue>
        <S.StatFoot>
          {missingMonths > 0
            ? `${missingMonths} ${missingMonths === 1 ? "month" : "months"} not yet reported`
            : "Full year reported"}
        </S.StatFoot>
      </S.StatCard>

      {months.length > 0 && (
        <S.StatCard>
          <S.StatLabel>Highest month</S.StatLabel>
          <S.StatValue>
            {formatTonnes(months[peakIndex])}
            <span>{EMISSION_UNIT}</span>
          </S.StatValue>
          <S.StatFoot>{monthly[peakIndex].month}</S.StatFoot>
        </S.StatCard>
      )}
    </S.StatGrid>
  );
};

/**
 * One emission category as a card: colour-accented, with its share of the scope
 * total shown both as a bar and a percentage.
 * @param {string} props.name - category name
 * @param {number} props.value - category emissions in kg CO₂e
 * @param {number} props.total - scope total in kg CO₂e (share denominator)
 * @param {string} props.color - category series colour
 */
const CategoryStat = ({ name, value, total, color }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <S.StatCard $accent={color}>
      <S.StatLabel>
        <S.StatSwatch $color={color} />
        {name}
      </S.StatLabel>
      <S.StatValue>
        {formatTonnes(value)}
        <span>{EMISSION_UNIT}</span>
      </S.StatValue>
      <S.StatShare $pct={pct} $color={color}>
        <span />
      </S.StatShare>
      <S.StatFoot>{pct.toFixed(1)}% of scope total</S.StatFoot>
    </S.StatCard>
  );
};
